import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Stack } from "@mui/material";
import { useState } from "react";
import { ILoginForm } from "../../interfaces/login/ILoginForm";
import { Formik } from "formik";
import { LoginValidationSchema } from "./login.validation";
import Loader from "../../components/loader";
import { useNotification } from "../../components/notification/useNotification";
import { tryLogin } from "./login.service";
import { IUserTokenDto } from "../../interfaces/user/IUserTokenDto";
import extractErrorMessage from "../../common/utils/errorMessages";
import { useNavigate } from "react-router-dom";
import { getBaseURLWithAuth } from "../../common/utils/session";
import updateUserInformation from "../../common/utils/user";
import useLazyLoading from "../../common/hooks/loader";
import { tryToUpdateCryptoCurrencyPrices } from "../../services/crypto-currency/cripto-currency.service";

export default function LoginForm() {

    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const { displayNotification } = useNotification();
    const navigate = useNavigate();

    const initialValues: ILoginForm = {
        email: '',
        password: ''
    };

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    }

    return (
        <>
            {showLoading === true ? <Loader /> : null}
            <Formik
                initialValues={initialValues}
                validationSchema={LoginValidationSchema}
                onSubmit={async (values, { setStatus, setSubmitting }) => {
                    const login: ILoginForm = {
                        email: values.email,
                        password: values.password
                    }

                    try {
                        setLoading(true);
                        const response = (await tryLogin(login)).data;
                        updateUserInformation(response);

                        try {
                            var updatePricesResponse = await tryToUpdateCryptoCurrencyPrices();

                            if (updatePricesResponse.status === 200) {
                                displayNotification({ type: 'success', message: 'Preços actualizados com sucesso.' });
                            }

                        } catch (error) {
                            displayNotification({ type: 'error', message: extractErrorMessage(error) });
                        }

                        navigate(getBaseURLWithAuth() + 'dashboard');
                    } catch (error: any) {
                        setStatus(false);
                        setSubmitting(false);
                        displayNotification({ type: 'error', message: extractErrorMessage(error) })
                    } finally {
                        setLoading(false);
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                    <form noValidate onSubmit={handleSubmit}>
                        <Stack spacing={2.5}>

                            <Stack spacing={1}>
                                <InputLabel htmlFor="input-email">Email</InputLabel>
                                <OutlinedInput
                                    id="input-email"
                                    type="text"
                                    name="email"
                                    value={values.email}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    placeholder="Escreva aqui o seu email"
                                    error={Boolean(touched.email && errors.email)}
                                    fullWidth
                                />
                                {touched.email && errors.email && (
                                    <FormHelperText error id="helper-text-email-error">
                                        {errors.email}
                                    </FormHelperText>
                                )}
                            </Stack>

                            <Stack spacing={1}>
                                <InputLabel htmlFor="input-password">Password</InputLabel>
                                <OutlinedInput
                                    id="input-password"
                                    error={Boolean(touched.password && errors.password)}
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    name="password"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    placeholder="Escreva aqui a sua password"
                                    fullWidth
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                                size="large"
                                            >
                                                {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                {touched.password && errors.password && (
                                    <FormHelperText error id="helper-text-errors-password">
                                        {errors.password}
                                    </FormHelperText>
                                )}
                            </Stack>

                            <Button
                                color="primary"
                                size="large"
                                variant="contained"
                                fullWidth
                                type="submit"
                                disabled={Boolean(errors.email || errors.password)}
                                sx={{ mt: 1, py: 1.5 }}
                            >
                                Entrar
                            </Button>

                        </Stack>
                    </form>
                )}
            </Formik>
        </>
    )
}
